import React, {useState, useEffect} from "react";
import {ErrorMessage, Field, Form, Formik} from "formik";
import * as Yup from "yup";
import {
    Alert,
    Button,
    Col,
    FormControl,
    FormGroup,
    FormLabel,
    InputGroup,
    Modal,
    ModalTitle,
    Row,
    Spinner,
} from "react-bootstrap";
import {BsTrash, BsEnvelope, BsPlusLg, BsEye as Eye, BsEyeSlashFill as EyeSlashFill} from "react-icons/bs";
import StyledSelect from "../../components/StyledSelect";

const SaveUserDialog = ({
    saveErrorMessage,
    onSaveClicked,
    onUpdateClicked,
    onCancelClicked,
    onDeleteClicked,
    onResetPasswordClicked,
    resetPasswordSuccess,
    username,
    email,
    emailUsernameRepetition,
    role,
    organizationId,
    careUnits,
    opticians,
    loggedInUser,
    loggedInUserRole,
    loggedInUserOrganizationId,
    userForm,
    isLoading,
}) => {

    const isUpdateMode = !!username;

    const [confirmAction, setConfirmAction] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    // Clear confirmation state when async action completes (success or error)
    useEffect(() => {
        if (resetPasswordSuccess || saveErrorMessage) {
            setConfirmAction(null);
        }
    }, [resetPasswordSuccess, saveErrorMessage]);

    const userHasRole = (roleToCheck) => {
        return loggedInUserRole === roleToCheck;
    };

    const isAdminRole = userHasRole('ADMIN') || userHasRole('CARE_UNIT_ADMIN') || userHasRole('OPTICIAN_ADMIN');

    const getAvailableRoles = () => {
        const availableRoles = [];

        if (userHasRole('ADMIN')) {
            availableRoles.push(
                { value: 'CARE_UNIT_ADMIN', label: 'VÅRDENHET - ADMIN' },
                { value: 'CARE_UNIT', label: 'VÅRDENHET' },
                { value: 'OPTICIAN_ADMIN', label: 'OPTIKER - ADMIN' },
                { value: 'OPTICIAN', label: 'OPTIKER' },
                { value: 'ADMIN', label: 'ADMIN' }
            );
        } else if (userHasRole('CARE_UNIT_ADMIN')) {
            availableRoles.push(
                { value: 'CARE_UNIT_ADMIN', label: 'VÅRDENHET - ADMIN' },
                { value: 'CARE_UNIT', label: 'VÅRDENHET' },
                { value: 'OPTICIAN_ADMIN', label: 'OPTIKER - ADMIN' },
                { value: 'OPTICIAN', label: 'OPTIKER' }
            );
        } else if (userHasRole('OPTICIAN_ADMIN')) {
            availableRoles.push(
                { value: 'OPTICIAN_ADMIN', label: 'OPTIKER - ADMIN' },
                { value: 'OPTICIAN', label: 'OPTIKER' }
            );
        }

        return availableRoles;
    };

    const getAvailableOrganizations = (selectedRole) => {
        if (!selectedRole || selectedRole === 'ADMIN') return [];

        const isCareUnitRole = ['CARE_UNIT', 'CARE_UNIT_ADMIN'].includes(selectedRole);
        const isOpticianRole = ['OPTICIAN', 'OPTICIAN_ADMIN'].includes(selectedRole);
        const allCareUnits = careUnits || [];
        const allOpticians = opticians || [];

        if (userHasRole('ADMIN')) {
            return isCareUnitRole ? allCareUnits : allOpticians;
        }
        if (userHasRole('CARE_UNIT_ADMIN')) {
            if (isCareUnitRole) return allCareUnits.filter(c => c.organizationId === loggedInUserOrganizationId);
            if (isOpticianRole) return allOpticians.filter(o => o.careUnitOrganizationIds && o.careUnitOrganizationIds.includes(loggedInUserOrganizationId));
        }
        if (userHasRole('OPTICIAN_ADMIN') && isOpticianRole) {
            return allOpticians.filter(o => o.organizationId === loggedInUserOrganizationId);
        }

        return [];
    };

    const isLoggedInAsAdmin = userHasRole('ADMIN');

    const baseValidation = {
         firstName: Yup.string().max(70, "Max 70 tecken"),
         lastName: Yup.string().max(70, "Max 70 tecken"),
         username: Yup.string().required("Obligatoriskt fält"),
         email: Yup.string().email("Ogiltig e-post").max(254, "Max 254 tecken").required("Obligatoriskt fält"),
         emailRepetition: Yup.string().oneOf([Yup.ref("email")], "E-postadresserna matchar inte").required("Obligatoriskt fält"),
         role: Yup.string().required("Välj en roll"),
         organization: Yup.string().when('role', {
             is: (role) => role !== 'ADMIN',
             then: (schema) => schema.required("Välj en organisation"),
             otherwise: (schema) => schema.notRequired(),
         }),
    };

    const createValidationAdmin = {
         ...baseValidation,
         password: Yup.string().test('min-length', 'Lösenordet måste vara minst 8 tecken långt',
             (value) => !value || value.length >= 8),
         retypePassword: Yup.string().when('password', {
             is: (password) => password && password.length > 0,
             then: (schema) => schema.oneOf([Yup.ref("password")], "Lösenord matchar inte").required("Repetera lösenordet"),
             otherwise: (schema) => schema.notRequired(),
         }),
    };

    const getValidationSchema = () => {
        if (isUpdateMode) return baseValidation;
        if (isLoggedInAsAdmin) return createValidationAdmin;
        return baseValidation;
    };

    const validationSchema = Yup.object().shape(getValidationSchema());

    return (
        <Modal
            show={true}
            onHide={onCancelClicked}
            backdrop="static"
            keyboard={true}
            size="lg"
        >
            <div className="SaveUserDialog">
                <Modal.Header closeButton>
                    <ModalTitle>
                        <h3 className="mb-0">{isUpdateMode ? "Uppdatera användare" : "Skapa användare"}</h3>
                    </ModalTitle>
                </Modal.Header>
                <Modal.Body>
                    <Formik
                          initialValues={{
                            firstName: userForm.firstName || "",
                            lastName: userForm.lastName || "",
                            username: userForm.username || "",
                            email: userForm.email || "",
                            emailRepetition: userForm.emailRepetition || "",
                            role: userForm.role || "",
                            organization: userForm.organization || "",
                            password: userForm.password || "",
                            retypePassword: "",
                          }}
                          validationSchema={validationSchema}
                          onSubmit={(values) => {
                            if (onUpdateClicked) {
                              onUpdateClicked(values);
                            } else {
                              onSaveClicked(values);
                            }
                          }}
                    >
                    {({ isValid, touched, errors, values }) => (
                    <Form id="saveUserForm" className="SaveUserForm">
                        <fieldset disabled={isLoading}>
                            <FormGroup as={Row} className="SaveUserDialog-row align-items-center">
                                <FormLabel column sm={4}>Namn</FormLabel>
                                <Col sm={4}>
                                    <Field as={FormControl} type="text" name="firstName" placeholder="Förnamn" />
                                    <ErrorMessage name="firstName" component="div" className="text-danger small" />
                                </Col>
                                <Col sm={4}>
                                    <Field as={FormControl} type="text" name="lastName" placeholder="Efternamn" />
                                    <ErrorMessage name="lastName" component="div" className="text-danger small" />
                                </Col>
                            </FormGroup>

                            <FormGroup as={Row} className="SaveUserDialog-row align-items-center">
                                <FormLabel column sm={4}>Användarnamn</FormLabel>
                                <Col sm={8}>
                                    <Field as={FormControl} type="text" name="username" placeholder="Användarnamn" disabled={isUpdateMode} />
                                    <ErrorMessage name="username" component="div" className="text-danger small" />
                                </Col>
                            </FormGroup>

                            <FormGroup as={Row} className="SaveUserDialog-row align-items-center">
                                <FormLabel column sm={4}>Email-adress</FormLabel>
                                <Col sm={8}>
                                    <InputGroup>
                                        <InputGroup.Text className="SaveUserDialog-input-icon"><BsEnvelope /></InputGroup.Text>
                                        <Field as={FormControl} type="email" name="email" placeholder="Ex: namn@exempel.se" />
                                    </InputGroup>
                                    <ErrorMessage name="email" component="div" className="text-danger small" />
                                </Col>
                            </FormGroup>

                            <FormGroup as={Row} className="SaveUserDialog-row align-items-center">
                                <FormLabel column sm={4}>Repetera email</FormLabel>
                                <Col sm={8}>
                                    <InputGroup>
                                        <InputGroup.Text className="SaveUserDialog-input-icon"><BsEnvelope /></InputGroup.Text>
                                        <Field as={FormControl} type="email" name="emailRepetition" placeholder="Repetera email" />
                                    </InputGroup>
                                    <ErrorMessage name="emailRepetition" component="div" className="text-danger small" />
                                </Col>
                            </FormGroup>

                            <FormGroup as={Row} className="SaveUserDialog-row align-items-center">
                                <FormLabel column sm={4}>Roll</FormLabel>
                                <Col sm={8}>
                                    <Field name="role">
                                        {({field, form}) => (
                                            <StyledSelect
                                                value={field.value}
                                                placeholder="Välj roll..."
                                                options={getAvailableRoles()}
                                                onChange={(val) => {
                                                    form.setFieldValue('role', val);
                                                    form.setFieldValue('organization', '');
                                                }}
                                            />
                                        )}
                                    </Field>
                                    <ErrorMessage name="role" component="div" className="text-danger small" />
                                </Col>
                            </FormGroup>

                            {values.role && values.role !== 'ADMIN' && (
                                <FormGroup as={Row} className="SaveUserDialog-row align-items-center">
                                    <FormLabel column sm={4}>Organisation</FormLabel>
                                    <Col sm={8}>
                                        <Field name="organization">
                                            {({field, form}) => (
                                                <StyledSelect
                                                    value={field.value}
                                                    placeholder="Välj organisation..."
                                                    options={getAvailableOrganizations(values.role).map(o => ({
                                                        value: o.organizationId,
                                                        label: o.name,
                                                    }))}
                                                    onChange={(val) => form.setFieldValue('organization', val)}
                                                />
                                            )}
                                        </Field>
                                        <ErrorMessage name="organization" component="div" className="text-danger small" />
                                    </Col>
                                </FormGroup>
                            )}

                            {!username && isLoggedInAsAdmin && (
                                <>
                                    <FormGroup as={Row} className="SaveUserDialog-row align-items-center">
                                        <FormLabel column sm={4}>
                                            Lösenord
                                            <br/>
                                            <small className="text-muted fw-normal">Valfritt</small>
                                        </FormLabel>
                                        <Col sm={8}>
                                            <div className="SaveUserDialog-password-field">
                                                <Field as={FormControl} type={showPassword ? "text" : "password"} name="password"/>
                                                <Button onClick={() => setShowPassword(!showPassword)}>
                                                    {showPassword ? <EyeSlashFill/> : <Eye/>}
                                                </Button>
                                            </div>
                                            <small className="text-muted">
                                                Lämna tomt för att skicka aktiveringsmail
                                            </small>
                                            <ErrorMessage name="password" component="div" className="text-danger small" />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveUserDialog-row align-items-center">
                                        <FormLabel column sm={4}>Repetera lösenord</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type={showPassword ? "text" : "password"} name="retypePassword"/>
                                            <ErrorMessage name="retypePassword" component="div" className="text-danger small" />
                                        </Col>
                                    </FormGroup>
                                </>
                            )}

                            {!username && !isLoggedInAsAdmin && (
                                <Alert variant="info" className="mt-2">
                                    Användaren kommer att få ett aktiveringsmail för att sätta sitt lösenord.
                                </Alert>
                            )}
                        </fieldset>

                        {resetPasswordSuccess && (
                            <Alert variant="success" className="mt-2">
                                Lösenordet för <strong>{username}</strong> har återställts. Ett aktiveringsmail har skickats.
                            </Alert>
                        )}
                        {saveErrorMessage && <Alert variant="danger" className="mt-2">{saveErrorMessage}</Alert>}

                        {/* Footer */}
                        <div className="SaveUserDialog-footer">
                            {confirmAction === 'resetPassword' && (
                                <>
                                    <div className="SaveUserDialog-footer-message">
                                        Vill du återställa lösenordet? Aktiveringsmail skickas till <strong>{values.email}</strong>.
                                    </div>
                                    <div className="d-flex justify-content-end gap-2">
                                        <Button variant="danger" disabled={isLoading} onClick={() => setConfirmAction(null)}>
                                            Avbryt
                                        </Button>
                                        <Button variant="primary" disabled={isLoading} onClick={() => onResetPasswordClicked(username)}>
                                            {isLoading ? <><Spinner animation="border" size="sm"/> Återställer...</> : "Återställ lösenord"}
                                        </Button>
                                    </div>
                                </>
                            )}

                            {confirmAction === 'delete' && (
                                <>
                                    <div className="SaveUserDialog-footer-message">
                                        Vill du ta bort användaren <strong>{username}</strong>? Detta kan inte ångras.
                                    </div>
                                    <div className="d-flex justify-content-end gap-2">
                                        <Button variant="danger" disabled={isLoading} onClick={() => setConfirmAction(null)}>
                                            Avbryt
                                        </Button>
                                        <Button variant="primary" disabled={isLoading} onClick={() => onDeleteClicked(username)}>
                                            {isLoading ? <><Spinner animation="border" size="sm"/> Tar bort...</> : <><BsTrash style={{ marginRight: '6px' }}/>Ta bort användare</>}
                                        </Button>
                                    </div>
                                </>
                            )}

                            {!confirmAction && (
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex gap-3">
                                        {isUpdateMode && isAdminRole && (
                                            <>
                                                <Button
                                                    variant="link"
                                                    className="SaveUserDialog-link-btn"
                                                    onClick={() => setConfirmAction('resetPassword')}
                                                    disabled={isLoading}
                                                >
                                                    Återställ lösenord
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    className="SaveUserDialog-link-btn text-danger"
                                                    onClick={() => setConfirmAction('delete')}
                                                    disabled={isLoading}
                                                >
                                                    <BsTrash style={{ marginRight: '5px' }}/>Ta bort
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="danger"
                                            onClick={onCancelClicked}
                                            disabled={isLoading}
                                        >
                                            Avbryt
                                        </Button>
                                        {onUpdateClicked ? (
                                            <Button type="submit" variant="primary" disabled={isLoading}>
                                                {isLoading ? <><Spinner animation="border" size="sm"/> Uppdaterar...</> : "Uppdatera"}
                                            </Button>
                                        ) : (
                                            <Button type="submit" variant="primary" disabled={isLoading}>
                                                {isLoading ? <><Spinner animation="border" size="sm"/> Skapar...</> : <><BsPlusLg style={{ marginRight: '6px' }}/>Skapa</>}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Form>
                    )}
                    </Formik>
                </Modal.Body>
            </div>
        </Modal>
    );
};
export default SaveUserDialog;